/**
 * /vs — index page listing every head-to-head comparison.
 *
 * Hub page that internally links to all per-vendor /vs/:slug pages,
 * boosting SEO juice for each individual comparison.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import { ArrowLeft, ArrowRight, BarChart3 } from 'lucide-react';
import { COMPETITORS } from '../data/competitorData';

const BASE = 'https://cartieai.com';

const VsIndex = () => {
  const list = Object.values(COMPETITORS);
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="vs-index-page">
      <SEOHead
        title="CARTIEAI Comparisons — vs Datadog, Helicone, Cloudability, LangSmith, Vantage"
        description="Side-by-side comparisons of CARTIEAI vs every major FinOps + LLM-observability competitor. Honest capabilities, real pricing, no marketing spin."
        canonical={`${BASE}/vs`}
        keywords="cartie ai comparison, finops alternatives, llm observability alternatives, datadog alternative, helicone alternative, cloudability alternative"
        ogImage={`${BASE}/og/vs.png`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'CARTIEAI vs the alternatives',
          description: 'Head-to-head comparisons against every major FinOps and LLM observability tool.',
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: list.map((c, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: `CARTIEAI vs ${c.name}`,
              url: `${BASE}/vs/${c.slug}`,
            })),
          },
        }}
      />

      <nav className="sticky top-0 z-50 backdrop-blur-md bg-slate-950/80 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-200 hover:text-white" data-testid="vs-index-back">
            <ArrowLeft className="w-4 h-4" /> CARTIEAI
          </Link>
          <Link to="/pricing" data-testid="vs-index-cta">
            <Badge className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-3 py-1.5">
              Start 7-day free <ArrowRight className="w-3 h-3 ml-1.5" />
            </Badge>
          </Link>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-16">
        <div className="space-y-4">
          <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold">
            <BarChart3 className="w-3 h-3 mr-1.5 inline" /> Honest comparisons
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
            CARTIEAI <span className="text-emerald-300">vs the alternatives</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl">
            We don't hide the fact that other tools do some things better than us. Pick the comparison most relevant to your stack — we'll show you exactly where each tool wins, where we win, and what each one actually costs.
          </p>
          <Link to="/honest-scorecard" data-testid="vs-index-scorecard-link" className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-full text-sm font-semibold bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 transition-colors">
            <BarChart3 className="w-4 h-4" /> Prefer one big table? The 32-feature Honest Scorecard vs all 13 rivals <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 mt-12">
          {list.map((c) => (
            <Link key={c.slug} to={`/vs/${c.slug}`} data-testid={`vs-index-card-${c.slug}`}>
              <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-slate-800 hover:border-emerald-500/40 transition-colors h-full">
                <CardContent className="p-6">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-300 mb-2">{c.category}</div>
                  <h2 className="text-xl font-bold text-white mb-2">CARTIEAI <span className="text-slate-300">vs</span> {c.name}</h2>
                  <p className="text-sm text-slate-300 leading-relaxed">{c.subhead}</p>
                  <div className="flex items-center gap-2 mt-4 text-emerald-300 text-sm font-semibold">
                    See the full comparison <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
};

export default VsIndex;
